import React from 'react';
import makeClass from 'clsx';
import { Element, logger } from '@design-systems/utils';

export const themes = [
  'intuit',
  'turbotax',
  'turbo',
  'mint',
  'expert',
  'quickbooks',
  'quickbooksdesktop',
  'proconnect',
  'formfly'
] as const;

export const colorSchemeOptions = ['light', 'dark'] as const;

export type ThemeType = typeof themes[number];
export type ColorScheme = typeof colorSchemeOptions[number];

export interface Themeable {
  /** Render the component with a product theme. */
  theme?: ThemeType;
  /** The color scheme (light,dark) to style the component with */
  colorScheme?: ColorScheme;
}

export interface ThemeProviderProps {
  /** React node to be wrapped by ThemeProvider. */
  children: React.ReactNodeArray | undefined;
  /** Render the component with a product theme. */
  theme: ThemeType;
  /** The color scheme (light,dark) to style the component with */
  colorScheme: ColorScheme;
}

/**
 * Get all of the props for an HTML element + add the theme props.
 * Used to easily type the rest props of a component and add theme-ing.
 *
 * @example
 * export interface ButtonProps extends ThemeableElement<'button'> {
 *   size?: Sizes;
 * }
 */
export type ThemeableElement<T extends keyof JSX.IntrinsicElements> = Element<
  T
> &
  Themeable;

export const ThemeContext = React.createContext<Themeable>({
  theme: 'intuit',
  colorScheme: 'light'
});
export const Theme = ThemeContext.Provider;

/**
 * Wrapper functional component to inject theme to child components wrapping.
 *
 * @param props - should contain children, theme, and colorScheme
 */
export const ThemeProvider = (props: ThemeProviderProps) => {
  const { children, theme, colorScheme } = props;
  return (
    <ThemeContext.Provider value={{ theme, colorScheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

ThemeProvider.defaultProps = {
  children: undefined,
  colorScheme: 'light'
};

/**
 * Determines if provided theme name is a valid theme.
 *
 * @param theme - The theme to test.
 */
export const isValidTheme = (theme: ThemeType) => {
  if (themes.indexOf(theme) === -1) {
    logger.error(`Please provide one of following themes : ${themes}`);
    return false;
  }

  return true;
};

/**
 * getTheme utility to support to provide object containing currentTheme and currentColorScheme.
 * Needed for components being migrated from IDS and use alpha theme.
 *
 * @param props - see typescript definition
 * @param context - see typescript definition
 * @param styles - The css modules styles for the component
 */
export const getTheme = (
  props: Themeable,
  context: Themeable,
  styles: Record<string, string> = {}
) => {
  let currentTheme = props.theme || context.theme || 'intuit';

  // This is in place for legacy code that uses a "default" theme
  // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
  // @ts-ignore
  if (currentTheme === 'default') {
    currentTheme = 'intuit';
  }

  const currentColorScheme =
    props.colorScheme || context.colorScheme || 'light';

  const themeClass = makeClass(
    styles[currentTheme],
    styles[currentColorScheme]
  );

  return {
    currentTheme,
    currentColorScheme,
    themeClass
  };
};

/**
 * A hook to use the closest theme context.
 *
 * @param props - Current component props
 * @param styles - The css modules styles for the component
 *
 * @example
 * const { themeClass } = useTheme({ colorScheme, theme }, styles);
 */
export const useTheme = (
  props: Themeable,
  styles: Record<string, string> = {}
) => {
  const themeContext = React.useContext(ThemeContext);

  return getTheme(props, themeContext, styles);
};
